import React from 'react';

// §preview marketing interstitials, modelled on omzo's quiz overlays. Content
// is data-driven from each flow's `interstitials` (src/data/previewMeta/*):
//   chart       — projection chart + quote      (plain, accent, sub, chartTitle, yMax, yMin?, yUnit?, withLabel, withoutLabel, xLabels, rising?, quote)
//   trust       — press / rating card           (plain, accent, banner, rank, title, bullets)
//   curve       — "how it works" S-curve        (plain, accent, yLabel[], arrowLabel[], xLabels, bullets)
//   testimonial — quote + before/after figures  (quote: array of strings | [accentString])
//   team        — clinician card                 (plain, accent, name, role, photo, bio[], source?)
// They sit BETWEEN questions and collect nothing.
//
// The rating/customer-count lines reuse claims already on the site footer.
// Quotes and before/after figures are ILLUSTRATIVE (labelled on screen as
// "Illustrative example"), not real patients — swap in verified material via
// docs/BACKEND_INTAKE_ASSET_CMS_PRD.md.

const O = '#FB923C';
const OD = '#E67A2A';
const F = 'Red Hat Text, Inter, sans-serif';

const Headline = ({ plain, accent, sub }) => (
  <div className="mb-5">
    <h1 className="wl-headline">{plain} {accent ? <em>{accent}</em> : null}</h1>
    {sub && <p className="mt-2 text-base" style={{ color: OD }}>{sub}</p>}
  </div>
);
const NextButton = ({ onNext }) => (
  <button type="button" onClick={onNext} className="btn-primary mt-6" data-testid="interstitial-next">Next</button>
);
const Stars = () => (
  <div className="flex justify-center gap-1 mb-3" aria-label="5 stars">
    {[0, 1, 2, 3, 4].map((i) => (
      <svg key={i} className="w-6 h-6" viewBox="0 0 24 24" fill={O}><path d="M12 2l2.9 6.6 7.1.7-5.4 4.8 1.6 7L12 17.5 5.8 21l1.6-7L2 9.3l7.1-.7z" /></svg>
    ))}
  </div>
);
// ['plain', ['accent'], 'plain'] → spans
const Rich = ({ parts }) => (
  <>{(parts || []).map((p, i) => (Array.isArray(p) ? <em key={i}>{p[0]}</em> : <React.Fragment key={i}>{p}</React.Fragment>))}</>
);
const Sample = ({ text }) => <p className="text-sm mt-3 text-center" style={{ color: '#8a7d70' }}>{text}</p>;

const Chart = ({ s, onNext }) => {
  const rising = !!s.rising;
  // Treated curve: falling (weight) or rising (e.g. testosterone); untreated stays flat-ish.
  const treated = rising ? 'M80 210 C 180 190, 300 60, 540 30' : 'M80 30 C 180 60, 300 190, 540 210';
  const treatedFill = rising ? 'M80 210 C 180 190, 300 60, 540 30 L 540 210 Z' : 'M80 30 C 180 60, 300 190, 540 210 L 80 210 Z';
  const untreated = rising ? 'M80 210 C 200 200, 320 170, 540 160' : 'M80 30 C 200 40, 320 150, 540 90';
  const yTicks = [0, 1, 2, 3, 4];
  const yMax = parseFloat(s.yMax) || 20; const yMin = parseFloat(s.yMin) || 0; const unit = s.yUnit || (String(s.yMax).includes('%') ? '%' : '');
  return (
    <div data-testid={`ix-${s.id}`}>
      <Headline plain={s.plain} accent={s.accent} sub={s.sub} />
      <div className="rounded-3xl border bg-white p-5" style={{ borderColor: '#f3e6d8' }}>
        <p className="font-semibold text-gray-900 mb-3 text-center">{s.chartTitle}</p>
        <svg viewBox="0 0 560 260" className="w-full h-auto" role="img" aria-label={s.chartTitle}>
          <defs><linearGradient id={`fill-${s.id}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={O} stopOpacity=".35" /><stop offset="1" stopColor={O} stopOpacity="0" /></linearGradient></defs>
          {yTicks.map((i) => (
            <g key={i}>
              <line x1="70" x2="540" y1={30 + i * 45} y2={30 + i * 45} stroke="#efe3d6" strokeDasharray="4 6" />
              <text x="60" y={34 + i * 45} textAnchor="end" fontSize="13" fill="#8a7d70" fontFamily={F}>{Math.round(yMax - (i * (yMax - yMin)) / 4)}{unit}</text>
            </g>
          ))}
          <path d={untreated} fill="none" stroke="#9a8f84" strokeWidth="3" strokeDasharray="8 8" />
          <path d={treatedFill} fill={`url(#fill-${s.id})`} />
          <path d={treated} fill="none" stroke={O} strokeWidth="4" strokeLinecap="round" />
          <rect x="415" y={rising ? 176 : 52} width="120" height="24" rx="12" fill="#9a8f84" /><text x="475" y={rising ? 192 : 68} textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff" fontFamily={F}>{s.withoutLabel}</text>
          <rect x="430" y={rising ? 44 : 176} width="105" height="24" rx="12" fill={O} /><text x="482" y={rising ? 60 : 192} textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff" fontFamily={F}>{s.withLabel}</text>
          {(s.xLabels || []).map((l, i) => (
            <text key={l} x={80 + i * 153} y="245" textAnchor="middle" fontSize="14" fill="#8a7d70" fontFamily={F}>{l}</text>
          ))}
        </svg>
        {s.quote && (
          <div className="rounded-2xl p-5 mt-4 text-center" style={{ background: '#fff7ef' }}>
            <Stars />
            <p className="text-lg text-gray-900"><span className="font-semibold">{s.quote[0]}</span> {s.quote[1]}</p>
            <Sample text="Illustrative example — individual results vary." />
          </div>
        )}
      </div>
      <NextButton onNext={onNext} />
    </div>
  );
};

const Trust = ({ s, onNext }) => (
  <div data-testid={`ix-${s.id}`}>
    <Headline plain={s.plain} accent={s.accent} />
    <div className="rounded-3xl border bg-white overflow-hidden" style={{ borderColor: '#f3e6d8' }}>
      <div className="px-5 py-3 text-white font-bold" style={{ background: '#1F1B16' }}>{s.banner}</div>
      <div className="p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="text-3xl font-extrabold text-gray-900">{s.rank}</span>
            <span className="text-2xl font-bold text-gray-900">{s.title}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-lg px-2 py-1 text-white font-bold" style={{ background: '#2d8a5f' }}>4.8</span>
            <div><p className="text-xs font-semibold text-gray-900">Excellent</p><p className="text-xs" style={{ color: O }}>★★★★★</p></div>
          </div>
        </div>
        <ul className="mt-4 space-y-2 text-sm text-gray-700">
          {(s.bullets || []).map((b) => <li key={b} className="flex gap-2"><span style={{ color: O }}>✓</span> {b}</li>)}
        </ul>
      </div>
    </div>
    <NextButton onNext={onNext} />
  </div>
);

const Curve = ({ s, onNext }) => (
  <div data-testid={`ix-${s.id}`}>
    <Headline plain={s.plain} accent={s.accent} />
    <div className="rounded-3xl overflow-hidden border" style={{ borderColor: '#f3e6d8', background: 'linear-gradient(180deg,#fff 0%,#ffe8d1 100%)' }}>
      <svg viewBox="0 0 600 340" className="w-full h-auto" role="img" aria-label={`${s.plain} ${s.accent}`}>
        <line x1="0" x2="600" y1="60" y2="60" stroke="#1F1B16" strokeWidth="3" strokeDasharray="14 10" />
        <line x1="0" x2="600" y1="200" y2="200" stroke="#1F1B16" strokeWidth="3" strokeDasharray="14 10" />
        <path d="M0 270 C 120 270, 170 270, 230 220 S 330 70, 400 60 L 600 60" fill="none" stroke="#1F1B16" strokeWidth="6" strokeLinecap="round" />
        <circle cx="80" cy="270" r="10" fill="#fff" stroke="#1F1B16" strokeWidth="5" /><circle cx="400" cy="60" r="10" fill="#fff" stroke="#1F1B16" strokeWidth="5" />
        {(s.yLabel || []).map((t, i) => <text key={t} x="20" y={130 + i * 36} fontSize="30" fontWeight="700" fill="#1F1B16" fontFamily="Lora, serif">{t}</text>)}
        <line x1="375" x2="560" y1="118" y2="118" stroke="#1F1B16" strokeWidth="3" /><path d="M550 110l12 8-12 8" fill="none" stroke="#1F1B16" strokeWidth="3" />
        {(s.arrowLabel || []).map((t, i) => <text key={t} x="375" y={160 + i * 34} fontSize="28" fontWeight="700" fill="#1F1B16" fontFamily="Lora, serif">{t}</text>)}
        <line x1="0" x2="600" y1="296" y2="296" stroke="#1F1B16" strokeWidth="4" />
        {(s.xLabels || []).map((l, i) => (
          <g key={l}><line x1={80 + i * 145} x2={80 + i * 145} y1="290" y2="304" stroke="#1F1B16" strokeWidth="4" /><text x={80 + i * 145} y="330" textAnchor="middle" fontSize="20" fill="#1F1B16" fontFamily={F}>{l}</text></g>
        ))}
      </svg>
    </div>
    <ul className="mt-5 space-y-3 text-lg text-gray-800">
      {(s.bullets || []).map((b, i) => (
        <li key={i}>{Array.isArray(b) ? <>{b[0]}<span className="font-bold text-gray-900">{b[1]}</span></> : b}</li>
      ))}
    </ul>
    <NextButton onNext={onNext} />
  </div>
);

const Figure = ({ wide, label }) => (
  <div className="flex-1 rounded-2xl p-4 text-center" style={{ background: '#fff7ef' }}>
    <svg viewBox="0 0 160 240" className="w-full h-auto" aria-hidden="true">
      <circle cx="80" cy="42" r="28" fill={wide ? '#b8ab9d' : O} />
      <path d={wide ? 'M40 78h80c22 40 26 90 10 150H30c-16-60-12-110 10-150z' : 'M52 78h56c14 40 16 90 6 150H46c-10-60-8-110 6-150z'} fill={wide ? '#b8ab9d' : O} />
    </svg>
    <p className="mt-2 text-xs font-semibold uppercase tracking-wide" style={{ color: '#8a7d70' }}>{label}</p>
  </div>
);
// `image` (optional) replaces the illustrative silhouettes with a real
// progression image — the weight-loss flows use a DEXA body-composition series
// (public/preview/common/dexa-progress.jpg, four scans over 13 months).
const Testimonial = ({ s, onNext }) => (
  <div data-testid={`ix-${s.id}`}>
    <p className="wl-headline mb-5" style={{ fontSize: '1.4rem' }}><Rich parts={s.quote} /></p>
    {s.image ? (
      <div className="rounded-2xl overflow-hidden" style={{ background: '#000' }} data-testid="testimonial-image">
        <img src={s.image} alt={s.imageAlt || 'Progress over time'} className="w-full h-auto block" loading="lazy" />
      </div>
    ) : (
      <div className="flex gap-3">
        <Figure wide label="Before" />
        <Figure label="After" />
      </div>
    )}
    <Sample text={s.caption || 'Illustrative example — individual results vary.'} />
    <NextButton onNext={onNext} />
  </div>
);

// Meet-the-team card: real clinician photo + credentials + short bio (copy and
// photos from the practice's own About page; not testimonials).
const Team = ({ s, onNext }) => (
  <div data-testid={`ix-${s.id}`}>
    <Headline plain={s.plain} accent={s.accent} />
    <div className="rounded-3xl border bg-white overflow-hidden" style={{ borderColor: '#f3e6d8' }}>
      <div className="sm:flex">
        <img src={s.photo} alt={s.name} className="w-full sm:w-56 h-64 sm:h-auto object-cover object-top flex-shrink-0 bg-white" />
        <div className="p-5">
          <p className="text-xl font-bold text-gray-900">{s.name}</p>
          <p className="text-sm font-semibold mb-3" style={{ color: OD }}>{s.role}</p>
          {(s.bio || []).map((para, i) => <p key={i} className="text-sm text-gray-700 leading-relaxed mb-2">{para}</p>)}
          {s.source && <p className="text-xs mt-2" style={{ color: '#8a7d70' }}>{s.source}</p>}
        </div>
      </div>
    </div>
    <NextButton onNext={onNext} />
  </div>
);

const PreviewInterstitial = ({ step, onNext }) => {
  switch (step.kind) {
    case 'team': return <Team s={step} onNext={onNext} />;
    case 'chart': return <Chart s={step} onNext={onNext} />;
    case 'trust': return <Trust s={step} onNext={onNext} />;
    case 'curve': return <Curve s={step} onNext={onNext} />;
    case 'testimonial': return <Testimonial s={step} onNext={onNext} />;
    default: return null;
  }
};

export default PreviewInterstitial;
